import * as React from 'react';
import * as Sentry from '@sentry/react';

import { FetchError } from '@/services/api-service/api-service';
import { UFOExperienceContext } from '@/common/ufo';

import { getSession } from '../../../controllers/auth-controller';
import { RootStateDispatchContext, MessageError } from '../../../controllers/global-controller';

function sendErrorToSentry(error: MessageError) {
  return Sentry.withScope(scope => {
    scope.setTag('error-type', 'error-controller-hook');
    scope.setLevel(Sentry.Severity.Error);

    // create a custom sentry exception if not already error-like
    // sentry needs the capture exception type to match a generic error shape
    const exception = error instanceof Error ? error : new Error(error.message);

    // if it's just the default error we can enrich the error name by using
    // the error message
    exception.name = exception.name === 'Error' ? error.title : exception.name;

    // send exception and scope to sentry
    Sentry.captureException(exception, scope);
  });
}

/* FIXME: consider moving to a global state module */
function useErrorController() {
  const dispatch = React.useContext(RootStateDispatchContext);

  return React.useCallback(
    (msg: MessageError) => {
      getSession()
        ?.then(() => {
          // Error was not because of session expire.
          if (!msg.skipAnalysis) {
            sendErrorToSentry(msg);
          }

          dispatch({
            type: 'messages.error',
            data: msg,
          });
        })
        .catch(() => {
          // session is expired.
        });
    },
    [dispatch]
  );
}

export function useMessageErrorController(errorFormat: (err: Error) => MessageError) {
  const dispatch = React.useContext(RootStateDispatchContext);
  const ufoExperience = React.useContext(UFOExperienceContext);

  return React.useCallback(
    (err: Error | MessageError) => {
      // https://hello.atlassian.net/browse/PSREAU-1678 we no longer surface 404 to sentry
      const isFetchErrorUserError =
        err instanceof FetchError && (err.response.status === 400 || err.response.status === 404);

      // NOTE: We do not put this inside the promise callback
      //  because a misuse of UFOExperienceSuccess means the layout render success can happen
      //  if this ran asynchronously after a render, which is too easy to do
      //  (e.g. component is not going to be stuck in skeleton on error, but displays empty
      //  on isLoading=false, then started->success->failure which is broken, don't think we
      //  have a pattern for what happens for components in error state atm)
      //  hopefully this inconsistency won't exist once we can actually check for auth status
      //  with same-domain backend and frontend from the FetchError directly
      if (isFetchErrorUserError) {
        // because the ufoExperience here can be a GlobalPageLoadExperience
        // manually transition even experiences with 'until' defined by 'force'
        // will not transition if already in a final state (i.e. inline fetch failures)
        // noinspection JSIgnoredPromiseFromCall
        ufoExperience.success({ force: true });
      } else {
        // noinspection JSIgnoredPromiseFromCall
        ufoExperience.failure({ force: true });
      }

      getSession()
        ?.then(() => {
          const msg = err instanceof Error ? errorFormat(err) : err;
          // Error was not because of session expire.

          if (!isFetchErrorUserError) sendErrorToSentry(msg);

          // Avoid duplicated error message on 404 page
          // Reference: https://hello.atlassian.net/browse/TOME-1180
          // Note: Using the useLocation() hook won't work here as we need to
          // retrieve the redirected location in the callback
          if (window.location.pathname === '/404') return;

          dispatch({
            type: 'messages.error',
            data: msg,
          });
        })
        .catch(() => {
          // session is expired.
          // a redirect will happen very soon anyways.
        });
    },
    [dispatch, errorFormat, ufoExperience]
  );
}

// Form an error message from an Error with specialized handling of a FetchError
function fetchErrorFormat(err: Error): MessageError {
  return err instanceof FetchError
    ? {
        title: err.title || `Network Error (Status: ${err.response?.status || 'unknown'})`,
        message:
          err.message ||
          err.response?.statusText ||
          'Oops, looks like something went wrong. Please reach out to #help-tome on Slack.',
      }
    : {
        title: `Error: ${err.name}`,
        message: err.message,
      };
}

export function useFetchErrorController(): { onError: (err: Error | MessageError) => void } {
  return {
    onError: useMessageErrorController(fetchErrorFormat),
  };
}

export default useErrorController;
